import { ChartDataset } from 'chart.js';
import { Context } from 'chartjs-plugin-datalabels';
import { Color } from 'chartjs-plugin-datalabels/types/options';
import { ChartTypes, CONFIDENTIAL_VALUE } from '../constants/constants';
import { formatNumber } from '../utilFunctions/formatters';
import { FormatTypes } from '../v2/types';

enum Align {
  CENTER = 'center',
  START = 'start',
  END = 'end',
}

interface DataLabelOptions {
  rotation: (context: Context) => number;
  backgroundColor: (context: Context) => Color | null;
  color: (context: Context) => string;
  formatter: (value: string | number, context: Context) => string;
  align: (context: Context) => Align;
  anchor: (context: Context) => Align;
  offset: (context: Context) => number;
  borderRadius: (context: Context) => number;
}

const getDatasetsFromContext = (context: Context) => {
  return context.chart.data.datasets ?? [];
};

export const isConfidentialDataset = (ds: ChartDataset) =>
  ds.label === CONFIDENTIAL_VALUE;

const getNonConfidentialDatasetsFromContext = (context: Context) => {
  return getDatasetsFromContext(context).filter(
    (ds) => !isConfidentialDataset(ds)
  );
};

const getChartTypeFromContext = (context: Context) => {
  return context.chart.config.type;
};

const isChartStacked = (context: Context) => {
  const isBarChart = getChartTypeFromContext(context) === ChartTypes.BAR;
  return (isBarChart && context.chart.options.scales?.stacked) ?? false;
};

// Please note that threshold needs to be a number between 0-1;
const isAboveThresholdForDataset = (
  dataSet: any[],
  datum: number,
  threshold: number
) => {
  const maxValue = Math.max(...dataSet);
  return datum >= threshold * maxValue;
};

const DATALABEL_ALIGN_THRESHOLD = 0.3;
const DATALABEL_STACK_BAR_SHOW_NUMBER_THRESHOLD = 0.1;

const labelFontColor = 'rgb(0,0,0)';
export const DataLabelDefaults: Record<ChartTypes, DataLabelOptions> = {
  [ChartTypes.BAR]: {
    rotation: (context: Context) => {
      const datasets = getNonConfidentialDatasetsFromContext(context);
      if (datasets && datasets.length > 1) {
        return 0;
      }
      return -90;
    },
    backgroundColor: () => {
      return null;
    },
    color: () => {
      return labelFontColor;
    },
    formatter: (value: string | number, context: Context) => {
      return `${formatNumber(value, FormatTypes.COUNT)}`;
    },
    align: (context: Context) => {
      const data = context.dataset.data ?? [];
      const datum: number = Number(data[context.dataIndex]);
      return isChartStacked(context)
        ? Align.CENTER
        : isAboveThresholdForDataset(data, datum, DATALABEL_ALIGN_THRESHOLD)
          ? Align.START
          : Align.END;
    },
    anchor: (context: Context) => {
      return isChartStacked(context) ? Align.CENTER : Align.END;
    },
    offset: (context: Context) => {
      return 0;
    },
    borderRadius: (context: Context) => {
      return 4;
    },
  },
  [ChartTypes.HORIZONTALBAR]: {
    rotation: (context: Context) => {
      const datasets = getNonConfidentialDatasetsFromContext(context);
      if (datasets && datasets.length > 1) {
        return 0;
      }
      return -90;
    },
    backgroundColor: () => {
      return null;
    },
    color: () => {
      return labelFontColor;
    },
    formatter: (value: string | number, context: Context) => {
      return `${formatNumber(value, FormatTypes.COUNT)}`;
    },
    align: (context: Context) => {
      const data = context.dataset.data ?? [];
      const datum: number = Number(data[context.dataIndex]);
      return isChartStacked(context)
        ? Align.CENTER
        : isAboveThresholdForDataset(data, datum, DATALABEL_ALIGN_THRESHOLD)
          ? Align.START
          : Align.END;
    },
    anchor: (context: Context) => {
      return isChartStacked(context) ? Align.CENTER : Align.END;
    },
    offset: (context: Context) => {
      return 0;
    },
    borderRadius: (context: Context) => {
      return 4;
    },
  },
  [ChartTypes.DOUGHNUT]: {
    rotation: (context: Context) => {
      return 0;
    },
    backgroundColor: () => {
      return null;
    },
    color: () => {
      return labelFontColor;
    },
    formatter: (value: string | number, context: Context) => {
      return `${formatNumber(value, FormatTypes.COUNT)}`;
    },
    align: (context: Context) => {
      return Align.CENTER;
    },
    anchor: (context: Context) => {
      return Align.CENTER;
    },
    offset: (context: Context) => {
      return 0;
    },
    borderRadius: (context: Context) => {
      return 4;
    },
  },
  [ChartTypes.LINE]: {
    rotation: (context: Context) => {
      return 0;
    },
    backgroundColor: () => {
      return null;
    },
    color: () => {
      return labelFontColor;
    },
    formatter: (value: string | number, context: Context) => {
      return `${formatNumber(value, FormatTypes.COUNT)}`;
    },
    align: (context: Context) => {
      const data = context.dataset.data ?? [];
      const datum: number = Number(data[context.dataIndex]);
      return isAboveThresholdForDataset(data, datum, DATALABEL_ALIGN_THRESHOLD)
        ? Align.START
        : Align.END;
    },
    anchor: (context: Context) => {
      return Align.END;
    },
    offset: (context: Context) => {
      return 0;
    },
    borderRadius: (context: Context) => {
      return 4;
    },
  },
};
